// frontend/src/components/RequestList.tsx
// Версия: 2.5.1 (2026-09-10) - список подписантов из реестра duty_operators
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.5.1: Select «Подписант» получает список из useDutyOperators
// ИЗМЕНЕНИЯ v2.5.0 (2026-02-06):
//   - БЛОК A ТОЧКА 4: handlePreviewConfirmation async, загружает raw_message из signal
//   - БЛОК A ТОЧКА 4: generatePreviewHtml принимает rawMessage параметр
//   - БЛОК B СЛОЙ 2: intervalRef (useRef) для остановки автообновления при 401
//   - БЛОК B СЛОЙ 3: isTokenValid() — проактивная проверка JWT перед fetch
//   - БЛОК B СЛОЙ 2c: catch 401 → clearInterval + redirect /login
//   - useRef добавлен в импорт React
//   - ВСЕ СУЩЕСТВУЮЩИЕ ФУНКЦИИ СОХРАНЕНЫ БЕЗ ИЗМЕНЕНИЙ
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.4.4 (2026-01-30):
//   - Добавлен operatorInfo state (localStorage 'operator_info')
//   - handleConfirmRequest(): POST /confirmations/prepare/:id теперь передаёт body.operatorName
//   - handleConfirmRequest(): POST /confirmations/:id/send теперь передаёт body.operator_name (для подписи PDF)
//   - Backward-compatible fallback: 'Оперативный дежурный ГМСКЦ'
//   - ВСЕ СУЩЕСТВУЮЩИЕ ФУНКЦИИ СОХРАНЕНЫ БЕЗ ИЗМЕНЕНИЙ
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.4.2 (2026-01-25) - FIX TM-003: URL-паттерны для Training Mode fetch-router
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.4.2:
//   - FIX TM-003: handleConfirmRequest() - URL изменён на /confirmations/{id}/send
//   - FIX TM-003: handleConfirmRequest() - устойчивое извлечение confirmationId
//   - FIX TM-003: confirmReject() - первичный POST /confirmations/{id}/reject + fallback
//   - Без изменения бизнес-логики: все остальные функции сохранены
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.4.1:
//   - FIX: Все fetch('/api/...') переведены на apiUrl('/api/...') (Stage/BASE_PATH)
//   - FIX: Устранён двойной initial-fetch (didInitialLoad guard)
//   - Без изменения бизнес-логики: пагинация/фильтры/карточка заявки/confirm/reject сохранены
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.4.0:
//   - Добавлена отдельная колонка IMO (была только в tooltip)
//   - Добавлен клик по строке → открывает Dialog с карточкой заявки
//   - Добавлен Dialog "Карточка заявки" с полными данными
//   - ВСЕ СУЩЕСТВУЮЩИЕ ФУНКЦИИ СОХРАНЕНЫ БЕЗ ИЗМЕНЕНИЙ
// ═══════════════════════════════════════════════════════════════════════════
// ИЗМЕНЕНИЯ v2.3.0:
//   - Добавлена серверная пагинация (limit=50, "Загрузить ещё")
//   - Добавлен фильтр по статусу (all/active/completed/failed)
//   - Добавлено поле поиска по судну/MMSI/терминалу
//   - Решена проблема падения браузера при 2681 записях
//   - ВСЕ СУЩЕСТВУЮЩИЕ ФУНКЦИИ СОХРАНЕНЫ БЕЗ ИЗМЕНЕНИЙ
// ═══════════════════════════════════════════════════════════════════════════
// ИСХОДНАЯ ВЕРСИЯ: 2.2.0 - Полнофункциональный компонент
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  LinearProgress,
  Divider,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  DirectionsBoat as BoatIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { apiUrl } from '../config/paths';
import {
  getRequestStatusLabel,
  getRequestStatusColor,
  normalizeRequestStatus,
} from '../utils/statusLabels';
import { OPERATORS_VERSION } from '../constants/operators.const';
import { formatTimeWithDate } from '../utils/formatTestInterval';
import { useDutyOperators } from '../hooks/useDutyOperators';

function formatUtcDateTime(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    return (
      new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  } catch {
    return dateStr;
  }
}

function toUtcDateTimeLocalValue(date: Date): string {
  return date.toISOString().slice(0, 16);
}

function parseUtcDateTimeLocal(value?: string): Date | null {
  if (!value) return null;
  const normalized = value.endsWith('Z') ? value : `${value}:00Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

// ═══════════════════════════════════════════════════════════════════════════
// ИНТЕРФЕЙСЫ (БЕЗ ИЗМЕНЕНИЙ)
// ═══════════════════════════════════════════════════════════════════════════
interface Request {
  id: number;
  request_number?: string;
  vessel_name?: string;
  mmsi?: string;
  imo_number?: string;
  ssas_number?: string;
  owner_organization?: string;
  contact_person?: string;
  contact_email?: string;
  additional_emails?: string[] | string | null;
  additionalEmails?: string[] | string | null;
  contact_phone?: string;
  test_date?: string;
  planned_test_date?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  notes?: string;
  rejection_reason?: string;
  source?: string;
}

function formatAdditionalEmails(value: unknown): string {
  if (Array.isArray(value)) {
    const emails = value.map((item) => String(item).trim()).filter(Boolean);
    return emails.length > 0 ? emails.join(', ') : '-';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '-';

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return formatAdditionalEmails(parsed);
      } catch {
        // Fall through to rendering the original string.
      }
    }

    return trimmed;
  }

  return '-';
}

interface ConfirmDialogData {
  open: boolean;
  request: Request | null;
  loading: boolean;
}

interface PeriodStats {
  today: number;
  week: number;
  month: number;
  total: number;
  active: number;
  completed: number;
  failed: number;
}

// ДОБАВЛЕНО v2.4.4: тип для operator_info из localStorage
interface OperatorInfo {
  id?: string;
  fullName?: string;
  shortName?: string;
  email?: string;
  setAt?: string;
  _v?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// ДОБАВЛЕНО v2.3.0: Константы пагинации
// ═══════════════════════════════════════════════════════════════════════════
const PAGE_SIZE = 50;
const AUTO_REFRESH_INTERVAL = 30000; // 30 секунд

// ═══════════════════════════════════════════════════════════════════════════
// HELPER: getAuthHeaders (БЕЗ ИЗМЕНЕНИЙ)
// ═══════════════════════════════════════════════════════════════════════════
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const DUTY_OPERATOR_REQUIRED_MESSAGE = 'Укажите ФИО оперативного дежурного в текущем дежурстве.';

const GENERIC_OPERATOR_NAMES = new Set([
  'unknown',
  'оператор',
  'оператор ссто',
  'оперативный дежурный',
  'оперативный дежурный гмскц',
]);

const normalizeOperatorName = (value?: string | null): string | null => {
  const normalized = value?.trim().replace(/\s+/g, ' ');
  if (!normalized) return null;
  return normalized;
};

const isGenericOperatorName = (value: string): boolean =>
  GENERIC_OPERATOR_NAMES.has(value.toLocaleLowerCase('ru-RU'));

const normalizeOperatorInfo = (operator: OperatorInfo | null | undefined): OperatorInfo | null => {
  const fullName = normalizeOperatorName(operator?.fullName);
  if (!fullName || isGenericOperatorName(fullName)) return null;

  return {
    ...operator,
    fullName,
    shortName: normalizeOperatorName(operator?.shortName) || fullName,
    _v: OPERATORS_VERSION,
  };
};

const readStoredOperatorInfo = (): OperatorInfo | null => {
  const saved = localStorage.getItem('operator_info');
  if (!saved) return null;

  try {
    const parsed = JSON.parse(saved) as OperatorInfo;
    if (parsed._v !== OPERATORS_VERSION) {
      localStorage.removeItem('operator_info');
      return null;
    }
    return normalizeOperatorInfo(parsed);
  } catch {
    localStorage.removeItem('operator_info');
    return null;
  }
};

const persistOperatorInfo = (operator: OperatorInfo) => {
  localStorage.setItem('operator_info', JSON.stringify({ ...operator, _v: OPERATORS_VERSION }));
};

const clearOperatorInfo = () => {
  localStorage.removeItem('operator_info');
};

const isCurrentDutyOperatorSelected = (operator: OperatorInfo | null): boolean =>
  Boolean(normalizeOperatorInfo(operator));

// ═══════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ RequestList
// ═══════════════════════════════════════════════════════════════════════════
const RequestList: React.FC = () => {
  const { operators: dutyOperators } = useDutyOperators(); // v2.5.1: реестр ОД из БД

  // ─── СУЩЕСТВУЮЩИЕ СОСТОЯНИЯ (БЕЗ ИЗМЕНЕНИЙ) ───
  const [requests, setRequests] = useState<Request[]>([]);
  const requestsRef = useRef<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogData>({
    open: false,
    request: null,
    loading: false,
  });
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    request: Request | null;
    reason: string;
    loading: boolean;
  }>({
    open: false,
    request: null,
    reason: '',
    loading: false,
  });
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    html: string;
    request: Request | null;
  }>({
    open: false,
    html: '',
    request: null,
  });
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // ─── ДОБАВЛЕНО v2.4.4: оператор (дежурный) из localStorage ───
  // v2.4.5: проверка OPERATORS_VERSION — сброс при обновлении списка
  const [operatorInfo, setOperatorInfo] = useState<OperatorInfo | null>(() =>
    readStoredOperatorInfo()
  );

  // ДОБАВЛЕНО v2.4.4: обновление operatorInfo при изменении localStorage (в других вкладках/окнах)
  // FIX-OPERATOR-PDF: + operatorChanged для синхронизации в ТЕКУЩЕЙ вкладке
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'operator_info') return;
      setOperatorInfo(readStoredOperatorInfo());
    };

    // FIX-OPERATOR-PDF: StorageEvent не срабатывает в той же вкладке
    const onOperatorChanged = (event: Event) => {
      const selectedOperator = normalizeOperatorInfo(
        (event as CustomEvent<OperatorInfo | null>).detail
      );
      setOperatorInfo(selectedOperator || readStoredOperatorInfo());
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('operatorChanged', onOperatorChanged as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('operatorChanged', onOperatorChanged as EventListener);
    };
  }, []);

  // FIX: Выбор подписанта для роли admin
  const currentRole = localStorage.getItem('role') || 'operator';
  const isAdmin = currentRole === 'admin';
  const [signerForConfirm, setSignerForConfirm] = useState<string>('');
  const isConfirmationDutyMissing = !isAdmin && !isCurrentDutyOperatorSelected(operatorInfo);

  const handleOpenDutyOperatorDialog = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openDutyOperatorDialog'));
  }, []);

  const loadCurrentDutyOperator = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/admin/settings/duty-operator'), {
        headers: getAuthHeaders(),
      });
      if (!response.ok) return;

      const data = await response.json();
      const dutyOperator = normalizeOperatorInfo(data?.operator);
      if (dutyOperator) {
        persistOperatorInfo(dutyOperator);
        setOperatorInfo(dutyOperator);
      } else {
        clearOperatorInfo();
        setOperatorInfo(null);
        window.dispatchEvent(new CustomEvent('operatorChanged', { detail: null }));
      }
    } catch {
      // Layout already shows the duty prompt; backend remains the source of truth.
    }
  }, []);

  useEffect(() => {
    loadCurrentDutyOperator().catch(() => undefined);
  }, [loadCurrentDutyOperator]);

  // ─── ДОБАВЛЕНО v2.3.0: СОСТОЯНИЯ ПАГИНАЦИИ ───
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>(''); // Для debounce

  // ─── ДОБАВЛЕНО v2.4.0: СОСТОЯНИЯ КАРТОЧКИ ЗАЯВКИ ───
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  // ─── ДОБАВЛЕНО v2.6.1: Время получения сигнала для карточки заявки ───
  const [signalDetectionTime, setSignalDetectionTime] = useState<string | null>(null);
  const [signalLoading, setSignalLoading] = useState(false);

  // ─── ДОБАВЛЕНО v2.4.1: guard от двойного initial-fetch ───
  const [didInitialLoad, setDidInitialLoad] = useState(false);

  // ─── ДОБАВЛЕНО v2.5.0: intervalRef для 401 fix (БЛОК B СЛОЙ 2) ───
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const fetchRequestsRef = useRef<(reset?: boolean) => Promise<void>>(null!);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER: getShiftPeriod - Период текущего дежурства (05:00 UTC → 05:00 UTC)
  // ═══════════════════════════════════════════════════════════════════════════
  const getShiftPeriod = useCallback(() => {
    const now = new Date();
    const currentHour = now.getUTCHours();

    let shiftStart: Date;
    let shiftEnd: Date;

    if (currentHour >= 5) {
      shiftStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 5, 0, 0)
      );
      shiftEnd = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 5, 0, 0)
      );
    } else {
      shiftStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1, 5, 0, 0)
      );
      shiftEnd = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 5, 0, 0)
      );
    }

    return {
      from: toUtcDateTimeLocalValue(shiftStart),
      to: toUtcDateTimeLocalValue(shiftEnd),
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ДОБАВЛЕНО v2.5.0: isTokenValid — проактивная проверка JWT (БЛОК B СЛОЙ 3)
  // ═══════════════════════════════════════════════════════════════════════════
  const isTokenValid = useCallback((): boolean => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1]));
      // Проверяем: если до истечения <60 секунд — считаем невалидным
      if (payload.exp && payload.exp * 1000 < Date.now() + 60000) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ИЗМЕНЕНО v2.3.0: fetchRequests с пагинацией
  // FIX v2.4.1: apiUrl() для Stage/BASE_PATH
  // FIX v2.5.0: isTokenValid + 401 handler (БЛОК B СЛОЙ 2c, 3)
  // ═══════════════════════════════════════════════════════════════════════════
  const fetchRequests = useCallback(
    async (reset: boolean = true) => {
      try {
        if (reset) {
          setLoading(true);
          setOffset(0);
          setRequests([]);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        // v2.5.0: Проактивная проверка токена перед запросом (БЛОК B СЛОЙ 3)
        if (!isTokenValid()) {
          console.warn('[RequestList] Токен истёк — останавливаю автообновление');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          window.location.href = (window as any).__BASE_PATH__
            ? `${(window as any).__BASE_PATH__}/login`
            : '/login';
          return;
        }

        const currentOffset = reset ? 0 : offset;

        // Строим URL с параметрами пагинации
        const params = new URLSearchParams();
        params.append('limit', PAGE_SIZE.toString());
        params.append('offset', currentOffset.toString());
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }
        if (searchQuery.trim()) {
          params.append('search', searchQuery.trim());
        }
        params.append('sortBy', 'created_at');
        params.append('sortOrder', 'DESC');
        const dateFromUtc = parseUtcDateTimeLocal(dateFrom);
        const dateToUtc = parseUtcDateTimeLocal(dateTo);
        if (dateFromUtc) params.append('dateFrom', dateFromUtc.toISOString());
        if (dateToUtc) params.append('dateTo', dateToUtc.toISOString());

        const url = apiUrl(`/requests?${params.toString()}`);
        console.log(`[RequestList] Fetching: ${url}`);

        const response = await fetch(url, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log(`[RequestList] Response:`, data);

        if (data.success) {
          const newItems = data.data || [];

          if (reset) {
            setRequests(newItems);
          } else {
            setRequests((prev) => [...prev, ...newItems]);
          }

          // Обновляем состояние пагинации
          if (data.total !== undefined) {
            setTotal(data.total);
            setHasMore(data.hasMore ?? currentOffset + newItems.length < data.total);
          } else {
            // Обратная совместимость: если total не пришёл, считаем что это все записи
            setTotal(newItems.length);
            setHasMore(false);
          }

          setOffset(currentOffset + newItems.length);
        } else {
          setError(data.error || 'Ошибка загрузки заявок');
        }
      } catch (err: any) {
        console.error('[RequestList] Fetch error:', err);
        // v2.5.0: JWT 401 FIX (БЛОК B СЛОЙ 2c)
        if (err.message?.includes('401')) {
          console.warn('[RequestList] 401 Unauthorized — останавливаю автообновление');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          window.location.href = (window as any).__BASE_PATH__
            ? `${(window as any).__BASE_PATH__}/login`
            : '/login';
          return;
        }
        setError(err.message || 'Ошибка соединения');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [offset, statusFilter, searchQuery, isTokenValid, dateFrom, dateTo]
  );

  // ─── FIX: синхронизация fetchRequestsRef при изменении fetchRequests (stale closure fix) ───
  useEffect(() => {
    fetchRequestsRef.current = fetchRequests;
  }, [fetchRequests]);

  // Синхронизация requestsRef
  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  // Тихое обновление без сброса скролла — для auto-refresh
  const softRefresh = useCallback(async () => {
    try {
      if (!isTokenValid()) return;

      const params = new URLSearchParams();
      params.append('limit', PAGE_SIZE.toString());
      params.append('offset', '0');
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('sortBy', 'created_at');
      params.append('sortOrder', 'DESC');
      const dateFromUtc = parseUtcDateTimeLocal(dateFrom);
      const dateToUtc = parseUtcDateTimeLocal(dateTo);
      if (dateFromUtc) params.append('dateFrom', dateFromUtc.toISOString());
      if (dateToUtc) params.append('dateTo', dateToUtc.toISOString());

      const url = apiUrl(`/requests?${params.toString()}`);
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) return;
      const data = await response.json();
      const newItems = data.data || data.items || (Array.isArray(data) ? data : []);

      // Fingerprint: id + status + updated_at
      const fp = (items: any[]) =>
        items.map((r: any) => `${r.id}:${r.status}:${r.updated_at}`).join('|');

      if (fp(newItems) === fp(requestsRef.current)) {
        return; // Данные не изменились
      }

      // Тихий merge: обновить изменившиеся поля, добавить новые
      setRequests((prev) => {
        const map = new Map(newItems.map((r: any) => [r.id, r]));
        const merged = prev.map((r) => map.get(r.id) || r);
        const existingIds = new Set(prev.map((r) => r.id));
        const added = newItems.filter((r: any) => !existingIds.has(r.id));
        return added.length > 0 ? [...added, ...merged] : merged;
      });

      if (data.total !== undefined) setTotal(data.total);
      if (data.hasMore !== undefined) setHasMore(data.hasMore);
    } catch (err) {
      console.warn('[RequestList] softRefresh error:', err);
    }
  }, [statusFilter, searchQuery, dateFrom, dateTo, isTokenValid]);

  const softRefreshRef = useRef(softRefresh);
  useEffect(() => {
    softRefreshRef.current = softRefresh;
  }, [softRefresh]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ДОБАВЛЕНО v2.3.0: loadMore - загрузка следующей страницы
  // ═══════════════════════════════════════════════════════════════════════════
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchRequests(false);
    }
  }, [fetchRequests, loadingMore, hasMore]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ДОБАВЛЕНО v2.3.0: Debounce для поиска
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  // При изменении фильтров - перезагружаем данные
  // FIX v2.4.1: guard (чтобы не дублировать initial fetch)
  useEffect(() => {
    if (!didInitialLoad) return;
    fetchRequests(true);
  }, [didInitialLoad, statusFilter, searchQuery, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════════════
  // useEffect: Первоначальная загрузка и автообновление
  // v2.5.0: intervalRef вместо локальной переменной (БЛОК B СЛОЙ 2)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    fetchRequests(true).finally(() => setDidInitialLoad(true));

    // Автообновление каждые 30 секунд — тихий merge без сброса скролла
    intervalRef.current = setInterval(() => {
      // Не обновлять если оператор работает в диалоге
      if (document.querySelector('[role="dialog"]')) return;
      softRefreshRef.current();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════════════
  // СУЩЕСТВУЮЩИЙ: filteredRequests (ИЗМЕНЕНО v2.3.0 - фильтр периода)
  // Фильтрация по дате теперь на клиенте (период дежурства)
  // ═══════════════════════════════════════════════════════════════════════════
  const filteredRequests = useMemo(() => {
    let result = [...requests];

    // Фильтр по периоду (клиентский - для дежурства)
    if (dateFrom) {
      const from = parseUtcDateTimeLocal(dateFrom);
      result = result.filter((r) => {
        const created = r.created_at ? new Date(r.created_at) : null;
        return created && from && created >= from;
      });
    }
    if (dateTo) {
      const to = parseUtcDateTimeLocal(dateTo);
      result = result.filter((r) => {
        const created = r.created_at ? new Date(r.created_at) : null;
        return created && to && created <= to;
      });
    }

    return result;
  }, [requests, dateFrom, dateTo]);

  // ═══════════════════════════════════════════════════════════════════════════
  // СУЩЕСТВУЮЩИЙ: statistics (БЕЗ ИЗМЕНЕНИЙ)
  // ═══════════════════════════════════════════════════════════════════════════
  const statistics: PeriodStats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const isToday = (dateStr?: string) => {
      if (!dateStr) return false;
      return new Date(dateStr) >= todayStart;
    };
    const isThisWeek = (dateStr?: string) => {
      if (!dateStr) return false;
      return new Date(dateStr) >= weekStart;
    };
    const isThisMonth = (dateStr?: string) => {
      if (!dateStr) return false;
      return new Date(dateStr) >= monthStart;
    };

    const today = filteredRequests.filter((r) => isToday(r.created_at)).length;
    const week = filteredRequests.filter((r) => isThisWeek(r.created_at)).length;
    const month = filteredRequests.filter((r) => isThisMonth(r.created_at)).length;

    const active = filteredRequests.filter((r) => {
      const n = normalizeRequestStatus(r.status || '');
      return n === 'pending' || n === 'approved';
    }).length;
    const completed = filteredRequests.filter(
      (r) => normalizeRequestStatus(r.status || '') === 'confirmed'
    ).length;
    const failed = filteredRequests.filter((r) => {
      const n = normalizeRequestStatus(r.status || '');
      return n === 'failed' || n === 'rejected' || n === 'cancelled';
    }).length;

    return {
      today,
      week,
      month,
      total: filteredRequests.length,
      active,
      completed,
      failed,
    };
  }, [filteredRequests]);

  // ═══════════════════════════════════════════════════════════════════════════
  // СУЩЕСТВУЮЩИЕ HANDLERS (БЕЗ ИЗМЕНЕНИЙ)
  // ═══════════════════════════════════════════════════════════════════════════
  const handleSetCurrentShift = () => {
    const period = getShiftPeriod();
    setDateFrom(period.from);
    setDateTo(period.to);
  };

  const handleClearPeriod = () => {
    setDateFrom('');
    setDateTo('');
  };

  const generateReport = () => {
    const reportData = filteredRequests.map((r) => ({
      Номер: r.request_number || r.id,
      Судно: r.vessel_name || '-',
      MMSI: r.mmsi || '-',
      IMO: r.imo_number || '-',
      Терминал: r.ssas_number || '-',
      Статус: getRequestStatusLabel(r.status || ''),
      'Дата создания': formatUtcDateTime(r.created_at),
    }));

    const headers = Object.keys(reportData[0] || {});
    let csv = headers.join(';') + '\n';
    reportData.forEach((row) => {
      csv += headers.map((h) => `"${(row as any)[h] || ''}"`).join(';') + '\n';
    });

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `requests_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenConfirmDialog = (request: Request) => {
    if (isConfirmationDutyMissing) {
      setSnackbar({
        open: true,
        message: DUTY_OPERATOR_REQUIRED_MESSAGE,
        severity: 'error',
      });
      handleOpenDutyOperatorDialog();
      return;
    }

    // FIX: Предзаполнить подписанта из текущего дежурства без generic fallback
    const defaultSigner = isAdmin ? signerForConfirm : operatorInfo?.fullName || '';
    setSignerForConfirm(defaultSigner);
    setConfirmDialog({ open: true, request, loading: false });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, request: null, loading: false });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ДОБАВЛЕНО v2.4.0: Обработчики карточки заявки
  // ═══════════════════════════════════════════════════════════════════════════
  const handleRowClick = (request: Request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
    // v2.6.1: Загрузка detection_time связанного сигнала
    setSignalDetectionTime(null);
    const normalizedStatus = normalizeRequestStatus(request.status || '');
    if (['approved', 'confirmed'].includes(normalizedStatus)) {
      setSignalLoading(true);
      fetch(apiUrl(`/signals?status=matched&limit=100`), { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data) return;
          const signals =
            data.items || data.data || data.signals || (Array.isArray(data) ? data : []);
          const matched = Array.isArray(signals)
            ? signals.find(
                (s: any) =>
                  s.request_id === request.id ||
                  (request.ssas_number && s.terminal_number === request.ssas_number)
              )
            : null;
          if (matched?.detection_time) {
            setSignalDetectionTime(matched.detection_time);
          }
        })
        .catch(() => {})
        .finally(() => setSignalLoading(false));
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedRequest(null);
    setSignalDetectionTime(null);
  };

  // v2.6.0: generatePreviewHtml — 1:1 соответствие реальному PDF (report.service.ts)
  const generatePreviewHtml = (request: Request, rawMessage: string = ''): string => {
    const now = new Date();
    const dateRu = now.toLocaleDateString('ru-RU');

    // Блок raw_message — как в report.service.ts
    const rawMessageBlock = rawMessage
      ? `
        <p><strong>Подтверждаем получение тестового сообщения ССТО:</strong></p>
        <div style="font-family: 'Courier New', monospace; white-space: pre-wrap; background: #f9f9f9; padding: 15px; border: 1px solid #ccc; margin: 15px 0; font-size: 11pt; line-height: 1.3;">${rawMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
      `
      : `
        <p><strong>Подтверждаем получение тестового сообщения ССТО:</strong></p>
        <div style="font-family: 'Courier New', monospace; white-space: pre-wrap; background: #f9f9f9; padding: 15px; border: 1px solid #ccc; margin: 15px 0; font-size: 11pt; line-height: 1.3;">Оригинальный текст сигнала недоступен</div>
      `;

    return `
      <div style="font-family: 'Times New Roman', serif; padding: 20px; max-width: 800px; line-height: 1.4; font-size: 12pt;">
        <!-- Шапка: органы власти -->
        <div style="text-align: center; margin-bottom: 20px;">
          <div style="font-size: 12pt; margin: 3px 0; font-weight: bold;">МИНТРАНС РОССИИ</div>
          <div style="font-size: 12pt; margin: 3px 0; font-weight: bold;">РОСМОРРЕЧФЛОТ</div>
        </div>

        <!-- Двухколоночная часть -->
        <div style="display: flex; justify-content: space-between; border-top: 1px solid #000; padding-top: 15px; margin-top: 20px;">
          <div style="width: 55%; font-size: 10pt; line-height: 1.3;">
            <div style="font-size: 10pt; margin-top: 10px;">
              ул. Петровка д. 3/6 стр. 2, г. Москва, 125993<br>
              тел.: (495) 626-10-52
            </div>
          </div>
          <div style="width: 40%; text-align: center; font-size: 12pt; font-weight: bold;">
            Главный морской<br>
            спасательно-<br>
            координационный центр<br>
            (ГМСКЦ)
          </div>
        </div>

        <!-- Номер документа -->
        <div style="margin: 25px 0; font-size: 11pt;">
          № _______ от ${dateRu}<br>
          На № ${request.request_number || '______'} от ________
        </div>

        <!-- Содержательная часть -->
        <div style="margin: 20px 0;">
          ${rawMessageBlock}
        </div>

        <!-- Подпись -->
        <div style="margin-top: 50px;">
          <div style="margin: 8px 0; font-size: 11pt;">Подписано ПЭП</div>
          <div style="margin: 8px 0; font-size: 11pt;">____________________</div>
          <div style="margin: 8px 0; font-size: 11pt;">Оперативный дежурный ГМСКЦ</div>
          <div style="margin: 8px 0; font-size: 11pt;">${dateRu}</div>
          <div style="margin: 8px 0; font-size: 11pt; margin-top: 15px;">Оперативный дежурный ГМСКЦ +7 (495) 626-10-52</div>
        </div>

        <!-- Футер -->
        <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #ccc; font-size: 10pt; color: #666;">
          Документ сгенерирован автоматически системой ССТО<br>
          <em>Предпросмотр — окончательный документ будет сформирован при отправке подтверждения</em>
        </div>
      </div>
    `;
  };

  // v2.7.0: Preview через backend endpoint — 1:1 соответствие отправляемому PDF
  const handlePreviewConfirmation = async (request: Request) => {
    try {
      const response = await fetch(apiUrl(`/confirmations/preview-draft/${request.id}`), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const html = await response.text();
        setPreviewDialog({ open: true, html, request });
        return;
      }
      // Fallback на локальную генерацию если endpoint недоступен
      console.warn('[RequestList] preview-draft endpoint failed, using local fallback');
    } catch (err) {
      console.warn('[RequestList] preview-draft fetch error, using local fallback:', err);
    }

    // Fallback: локальная генерация (старый код)
    let rawMessage = '';
    try {
      if ((request as any).signal_id) {
        const response = await fetch(apiUrl(`/signals/${(request as any).signal_id}`), {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const signal = await response.json();
          if (signal?.raw_message) rawMessage = signal.raw_message;
        }
      }
      if (!rawMessage) {
        const response = await fetch(apiUrl(`/signals?status=matched&limit=100`), {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          const signals =
            data.items || data.data || data.signals || (Array.isArray(data) ? data : []);
          const matchedSignal = Array.isArray(signals)
            ? signals.find(
                (s: any) =>
                  s.request_id === request.id ||
                  (request.ssas_number && s.terminal_number === request.ssas_number)
              )
            : null;
          if (matchedSignal?.raw_message) rawMessage = matchedSignal.raw_message;
        }
      }
    } catch (err) {
      console.warn('[RequestList] fallback raw_message load failed:', err);
    }
    const html = generatePreviewHtml(request, rawMessage);
    setPreviewDialog({ open: true, html, request });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FIX v2.4.2 TM-003: handleConfirmRequest - исправлены URL-паттерны
  // v2.4.4: prepare -> body.operatorName, send -> body.operator_name
  // ═══════════════════════════════════════════════════════════════════════════
  const handleConfirmRequest = async () => {
    if (!confirmDialog.request) return;

    if (isConfirmationDutyMissing) {
      setSnackbar({
        open: true,
        message: DUTY_OPERATOR_REQUIRED_MESSAGE,
        severity: 'error',
      });
      handleOpenDutyOperatorDialog();
      return;
    }

    setConfirmDialog((prev) => ({ ...prev, loading: true }));

    try {
      // Backend всё равно проверяет SystemSettings; body нужен только для совместимости API.
      const operatorName = isAdmin ? signerForConfirm : operatorInfo?.fullName;

      // Шаг 1: Подготовка подтверждения (v2.4.4: передаём operatorName)
      const prepareResponse = await fetch(
        apiUrl(`/confirmations/prepare/${confirmDialog.request.id}`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            operatorName,
          }),
        }
      );

      if (!prepareResponse.ok) {
        const errorData = await prepareResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка подготовки подтверждения');
      }

      // FIX v2.4.2: Устойчивое извлечение confirmationId из разных форматов ответа
      const prepareData = await prepareResponse.json().catch(() => null);
      const confirmationId =
        prepareData?.confirmation?.id ?? prepareData?.data?.id ?? prepareData?.id ?? null;

      const sendId = confirmationId || String(confirmDialog.request.id);

      // Шаг 2: Отправка подтверждения
      // FIX v2.4.2: URL изменён на /confirmations/{id}/send для совместимости с fetch-router
      // v2.4.4: передаём operator_name (snake_case) для подписи PDF на backend
      const sendResponse = await fetch(
        apiUrl(`/confirmations/${encodeURIComponent(sendId)}/send`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            operator_name: operatorName,
          }),
        }
      );

      if (!sendResponse.ok) {
        const errorData = await sendResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка отправки подтверждения');
      }

      setSnackbar({
        open: true,
        message: 'Подтверждение успешно отправлено',
        severity: 'success',
      });

      handleCloseConfirmDialog();
      fetchRequests(true);
    } catch (err: any) {
      console.error('[RequestList] Confirm error:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Ошибка подтверждения заявки',
        severity: 'error',
      });
    } finally {
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleRejectRequest = (request: Request) => {
    setRejectDialog({ open: true, request, reason: '', loading: false });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FIX v2.4.2 TM-003: confirmReject - исправлены URL-паттерны
  // Первичный: POST /confirmations/{id}/reject (перехватывается fetch-router)
  // Fallback: PUT /requests/{id}/status (для совместимости с prod backend)
  // ═══════════════════════════════════════════════════════════════════════════
  const confirmReject = async () => {
    if (!rejectDialog.request) return;

    setRejectDialog((prev) => ({ ...prev, loading: true }));

    try {
      const rid = rejectDialog.request.id;
      const reason = rejectDialog.reason || 'Отклонено оператором';

      // FIX v2.4.2: Первичный путь - POST /confirmations/{id}/reject
      // Этот URL перехватывается fetch-router в Training Mode
      // FIX: Admin использует выбранного подписанта (если диалог был открыт)
      const operatorShortName = isAdmin
        ? signerForConfirm || operatorInfo?.shortName || 'Оперативный дежурный ГМСКЦ'
        : operatorInfo?.shortName || 'Оперативный дежурный ГМСКЦ';
      let response = await fetch(apiUrl(`/confirmations/${rid}/reject`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          reason: reason,
          operator_name: operatorShortName,
        }),
      });

      // Fallback для совместимости со старым backend API (prod/stage)
      if (!response.ok && (response.status === 404 || response.status === 405)) {
        console.log('[RequestList] Reject fallback to /requests/:id/status');
        response = await fetch(apiUrl(`/requests/${rid}/status`), {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            status: 'rejected',
            rejection_reason: reason,
          }),
        });
      }

      if (!response.ok) {
        throw new Error('Ошибка отклонения заявки');
      }

      setSnackbar({
        open: true,
        message: 'Заявка отклонена',
        severity: 'info',
      });

      setRejectDialog({ open: false, request: null, reason: '', loading: false });
      fetchRequests(true);
    } catch (err: any) {
      console.error('[RequestList] Reject error:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Ошибка отклонения заявки',
        severity: 'error',
      });
    } finally {
      setRejectDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ДОБАВЛЕНО v2.3.0: handleStatusFilterChange
  // ═══════════════════════════════════════════════════════════════════════════
  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 2 }}>
      {/* ═══ HEADER ═══ */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h5" component="h1">
          Заявки на тестирование ССТО
        </Typography>
        <Box data-print="hide" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={handleSetCurrentShift}
            size="small"
          >
            Текущее дежурство
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={generateReport}
            size="small"
          >
            Отчёт
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            size="small"
          >
            Печать
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setStatsDialogOpen(true)}
            size="small"
          >
            Статистика
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => fetchRequests(true)}
            disabled={loading}
            size="small"
          >
            Обновить
          </Button>
        </Box>
      </Box>

      {/* ═══ ДОБАВЛЕНО v2.3.0: ПАНЕЛЬ ФИЛЬТРОВ ═══ */}
      <Paper data-print="hide" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Поиск */}
          <TextField
            size="small"
            placeholder="Поиск по судну, MMSI, терминалу..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearchClear}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Фильтр статуса */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Статус</InputLabel>
            <Select value={statusFilter} label="Статус" onChange={handleStatusFilterChange}>
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="pending">ОЖИДАЕТ</MenuItem>
              <MenuItem value="approved">ОДОБРЕНА</MenuItem>
              <MenuItem value="confirmed">ПОДТВЕРЖДЕНА</MenuItem>
              <MenuItem value="failed">ОШИБКА</MenuItem>
              <MenuItem value="expired">ПРОСРОЧЕНА</MenuItem>
              <MenuItem value="cancelled">ОТМЕНЕНА</MenuItem>
              <MenuItem value="rejected">ОТКЛОНЕНА</MenuItem>
            </Select>
          </FormControl>

          {/* Фильтр периода */}
          <TextField
            size="small"
            type="datetime-local"
            label="С"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="По"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          {(dateFrom || dateTo) && (
            <Button size="small" startIcon={<ClearIcon />} onClick={handleClearPeriod}>
              Сбросить период
            </Button>
          )}

          {/* Статистика загрузки */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Показано: {filteredRequests.length} из {total}
            </Typography>
          </Box>
        </Box>
      </Paper>
      {/* ═══ ERROR ═══ */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══ LOADING ═══ */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Только для печати: предупреждение о пагинации */}
      <Box
        sx={{
          display: 'none',
          '@media print': { display: 'block', mt: 1, mb: 1, fontStyle: 'italic', fontSize: '10pt' },
        }}
      >
        Показано {filteredRequests.length} из {total} записей
        {filteredRequests.length < total && ' (не все записи отображены на экране)'}
      </Box>

      {/* ═══ TABLE ═══ */}
      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell>
                  <strong>№</strong>
                </TableCell>
                <TableCell>
                  <strong>Судно</strong>
                </TableCell>
                <TableCell>
                  <strong>IMO</strong>
                </TableCell>
                <TableCell>
                  <strong>MMSI</strong>
                </TableCell>
                <TableCell>
                  <strong>Терминал</strong>
                </TableCell>
                <TableCell>
                  <strong>Статус</strong>
                </TableCell>
                <TableCell>
                  <strong>Источник</strong>
                </TableCell>
                <TableCell>
                  <strong>Дата создания</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Действия</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      Заявки не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    onClick={() => handleRowClick(request)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {request.request_number || request.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{request.vessel_name || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {request.imo_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {request.mmsi || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {request.ssas_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRequestStatusLabel(request.status || '')}
                        color={getRequestStatusColor(request.status || '')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {request.source === 'WEB_FORM'
                        ? 'Веб-форма'
                        : request.source === 'MORSPAS'
                          ? 'Морспас'
                          : request.source === 'MORFLOT'
                            ? 'Морфлот'
                            : request.source || 'Не определён'}
                    </TableCell>
                    <TableCell>{formatUtcDateTime(request.created_at)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Предпросмотр">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handlePreviewConfirmation(request)}
                            disabled={
                              !['approved', 'matched'].includes(
                                normalizeRequestStatus(request.status || '')
                              )
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isAdmin ? 'Доступно только операторам' : 'Подтвердить'}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenConfirmDialog(request)}
                            disabled={
                              isAdmin ||
                              !['approved', 'matched'].includes(
                                normalizeRequestStatus(request.status || '')
                              )
                            }
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Отклонить">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectRequest(request)}
                            disabled={['confirmed', 'rejected', 'cancelled'].includes(
                              normalizeRequestStatus(request.status || '')
                            )}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* ═══ ДОБАВЛЕНО v2.3.0: КНОПКА "ЗАГРУЗИТЬ ЕЩЁ" ═══ */}
          {hasMore && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {loadingMore ? (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}
                >
                  <LinearProgress sx={{ width: '50%' }} />
                  <Typography variant="body2" color="text.secondary">
                    Загрузка...
                  </Typography>
                </Box>
              ) : (
                <Button variant="outlined" startIcon={<ExpandMoreIcon />} onClick={loadMore}>
                  Загрузить ещё ({total - offset} осталось)
                </Button>
              )}
            </Box>
          )}

          {/* Информация о пагинации */}
          {!hasMore && filteredRequests.length > 0 && (
            <Box sx={{ p: 1, textAlign: 'center', backgroundColor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary">
                Показаны все записи ({filteredRequests.length})
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}

      {/* ═══ ДОБАВЛЕНО v2.4.0: DIALOG: Карточка заявки ═══ */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Карточка заявки #{selectedRequest?.request_number || selectedRequest?.id}
          <IconButton
            aria-label="close"
            onClick={handleCloseDetailsDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box>
              {/* Статус */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1">Статус:</Typography>
                <Chip
                  label={getRequestStatusLabel(selectedRequest.status || '')}
                  color={getRequestStatusColor(selectedRequest.status || '')}
                  size="medium"
                />
              </Box>

              {/* Данные судна */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <BoatIcon color="primary" />
                  Данные судна
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Название судна
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRequest.vessel_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      IMO номер
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.imo_number || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      MMSI
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.mmsi || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Номер терминала ССТО
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.ssas_number || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Источник
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.source === 'WEB_FORM'
                        ? 'Веб-форма'
                        : selectedRequest.source === 'MORSPAS'
                          ? 'Морспас'
                          : selectedRequest.source === 'MORFLOT'
                            ? 'Морфлот'
                            : selectedRequest.source || 'Не определён'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Данные тестирования */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <ScheduleIcon color="primary" />
                  Данные тестирования
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Дата создания заявки
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDateTime(selectedRequest.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Интервал теста (UTC)
                    </Typography>
                    {selectedRequest.start_time || selectedRequest.end_time ? (
                      <Box>
                        <Typography variant="body1">
                          Начало:{' '}
                          {formatTimeWithDate(
                            selectedRequest.start_time,
                            selectedRequest.planned_test_date || selectedRequest.test_date
                          )}
                        </Typography>
                        <Typography variant="body1">
                          Окончание:{' '}
                          {formatTimeWithDate(
                            selectedRequest.end_time,
                            selectedRequest.planned_test_date || selectedRequest.test_date
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        {selectedRequest.planned_test_date
                          ? new Date(selectedRequest.planned_test_date).toLocaleString('ru-RU', {
                              timeZone: 'UTC',
                            }) + ' UTC'
                          : selectedRequest.test_date
                            ? new Date(selectedRequest.test_date).toLocaleString('ru-RU', {
                                timeZone: 'UTC',
                              }) + ' UTC'
                            : '-'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Последнее обновление
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDateTime(selectedRequest.updated_at)}
                    </Typography>
                  </Grid>
                  {/* v2.6.1: Время получения сигнала (только для matched/approved заявок) */}
                  {(signalDetectionTime || signalLoading) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Время получения сигнала
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'success.main', fontWeight: 'bold' }}
                      >
                        {signalLoading
                          ? 'Загрузка...'
                          : signalDetectionTime
                            ? new Date(signalDetectionTime).toLocaleString('ru-RU', {
                                timeZone: 'UTC',
                              }) + ' UTC'
                            : '-'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Контактная информация */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <PersonIcon color="primary" />
                  Контактная информация
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Организация
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.owner_organization || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.contact_email || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Дополнительная адресация
                        </Typography>
                        <Typography variant="body1">
                          {formatAdditionalEmails(
                            selectedRequest.additional_emails ?? selectedRequest.additionalEmails
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Телефон
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.contact_phone || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Причина отклонения — для rejected/failed */}
              {['rejected', 'failed'].includes(
                normalizeRequestStatus(selectedRequest.status || '')
              ) && (
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: 'error.light',
                    border: '1px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CancelIcon color="error" />
                    Причина отклонения
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="body1">
                    {selectedRequest.rejection_reason ||
                      selectedRequest.notes ||
                      'Причина не указана'}
                  </Typography>
                </Paper>
              )}

              {/* Примечания — для прочих статусов */}
              {selectedRequest.notes &&
                !['rejected', 'failed'].includes(
                  normalizeRequestStatus(selectedRequest.status || '')
                ) && (
                  <Paper sx={{ p: 2, backgroundColor: 'warning.light' }}>
                    <Typography variant="h6" gutterBottom>
                      Примечания
                    </Typography>
                    <Typography variant="body1">{selectedRequest.notes}</Typography>
                  </Paper>
                )}

              {/* Системная информация */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                <Typography variant="caption" color="text.secondary">
                  ID записи: {selectedRequest.id}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Закрыть</Button>
          {selectedRequest &&
            !['confirmed', 'rejected', 'cancelled'].includes(
              normalizeRequestStatus(selectedRequest.status || '')
            ) && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  handleCloseDetailsDialog();
                  handleRejectRequest(selectedRequest);
                }}
              >
                Отклонить
              </Button>
            )}
          {selectedRequest &&
            !isAdmin &&
            ['approved', 'matched'].includes(
              normalizeRequestStatus(selectedRequest.status || '')
            ) && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={isConfirmationDutyMissing}
                onClick={() => {
                  handleCloseDetailsDialog();
                  handleOpenConfirmDialog(selectedRequest);
                }}
              >
                Подтвердить
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Подтверждение ═══ */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Подтверждение тестирования</DialogTitle>
        <DialogContent>
          {confirmDialog.request && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Судно:</strong> {confirmDialog.request.vessel_name || '-'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>MMSI:</strong> {confirmDialog.request.mmsi || '-'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Терминал:</strong> {confirmDialog.request.ssas_number || '-'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {confirmDialog.request.contact_email || '-'}
              </Typography>
              {/* FIX: Выбор подписанта для роли admin */}
              {isAdmin && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="signer-select-label">Подписант (оперативный дежурный)</InputLabel>
                  <Select
                    labelId="signer-select-label"
                    value={signerForConfirm}
                    label="Подписант (оперативный дежурный)"
                    onChange={(e) => setSignerForConfirm(e.target.value)}
                  >
                    {dutyOperators.map((op) => (
                      <MenuItem key={op.id} value={op.shortName}>
                        {op.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Для оператора — показать текущего подписанта (readonly) */}
              {!isAdmin && operatorInfo && (
                <Alert severity="success" icon={false} sx={{ mt: 2 }}>
                  Подписант: <strong>{operatorInfo.fullName}</strong>
                </Alert>
              )}

              {isConfirmationDutyMissing && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleOpenDutyOperatorDialog}>
                      Открыть текущее дежурство
                    </Button>
                  }
                >
                  {DUTY_OPERATOR_REQUIRED_MESSAGE}
                </Alert>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Подтверждение будет отправлено на email заявителя
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={confirmDialog.loading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmRequest}
            disabled={confirmDialog.loading || isAdmin || isConfirmationDutyMissing}
            startIcon={confirmDialog.loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {confirmDialog.loading ? 'Отправка...' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Отклонение ═══ */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Отклонение заявки</DialogTitle>
        <DialogContent>
          {rejectDialog.request && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Судно:</strong> {rejectDialog.request.vessel_name || '-'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Причина отклонения"
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialog((prev) => ({ ...prev, open: false }))}
            disabled={rejectDialog.loading}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={rejectDialog.loading}
            startIcon={rejectDialog.loading ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {rejectDialog.loading ? 'Отклонение...' : 'Отклонить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Предпросмотр ═══ */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, html: '', request: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Предпросмотр подтверждения</DialogTitle>
        <DialogContent>
          <Box
            sx={{ border: '1px solid #ccc', p: 2, backgroundColor: 'white' }}
            dangerouslySetInnerHTML={{ __html: previewDialog.html }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, html: '', request: null })}>
            Закрыть
          </Button>
          {previewDialog.request &&
            ['approved', 'matched'].includes(
              normalizeRequestStatus(previewDialog.request.status || '')
            ) && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setPreviewDialog({ open: false, html: '', request: null });
                  if (previewDialog.request) handleOpenConfirmDialog(previewDialog.request);
                }}
              >
                Подтвердить и отправить
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Статистика ═══ */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Статистика заявок</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom>
              Временные периоды
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {statistics.today}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Сегодня
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {statistics.week}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  За неделю
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {statistics.month}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  За месяц
                </Typography>
              </Paper>
            </Box>

            <Typography variant="h6" gutterBottom>
              По статусам
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {statistics.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Активные
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {statistics.completed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Завершённые
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {statistics.failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Неуспешные
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="body1">
                <strong>Всего записей в БД:</strong> {total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Загружено: {requests.length} | Отфильтровано: {filteredRequests.length}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ SNACKBAR ═══ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestList;
